import { useEffect, useMemo } from 'react'
import { ALL_SEDI, DEFAULT_SEDE, applySedeTheme, getCurrentSede } from './sedi'
import { VerdeVercelliApp } from './sites/verde-vercelli/VerdeVercelliApp'
import { VerdeValsesia2App } from './sites/verde-valsesia-2/VerdeValsesia2App'
import { VerdeVercelli2App } from './sites/verde-vercelli-2/VerdeVercelli2App'
import SharePreventivoPage from './pages/SharePreventivoPage'
import ConfirmSlotPage from './pages/ConfirmSlotPage'
import CollaboraPage from './pages/CollaboraPage'
import ReviewPage from './reviews/ReviewPage'
import ReviewInvitePage from './reviews/ReviewInvitePage'
// Static on purpose: a public token page is never lazy (prod nginx 403s lazy `p-*` chunks without a session).
import ConsensoPage from './pages/consenso/ConsensoPage'
import { initCollector } from './analytics/collector'

// Injected by vite.config.ts via `define`. Non-empty in per-site builds,
// empty string in dev when no explicit dev:<site> script was used.
const BUILD_SLUG = import.meta.env.VITE_SITE_SLUG as string | undefined

// One-shot path dispatch — landings/ is a marketing SPA with no router,
// so we just sniff the URL once on mount. The only non-marketing route
// is the customer-facing preventivo share page; everything else still
// falls through to the sede landing below.
const sharePreventivoMatch =
  typeof window !== 'undefined'
    ? window.location.pathname.match(/^\/share\/preventivo\/([A-Za-z0-9_-]+)\/?$/)
    : null

const confirmSlotMatch =
  typeof window !== 'undefined'
    ? window.location.pathname.match(/^\/conferma\/([A-Za-z0-9_-]+)\/?$/)
    : null

// Contractor-facing "Collabora" page. Backend builds links as
// `https://<sede-domain>/collabora/<uuid>` (token is a UUID).
const collaboraMatch =
  typeof window !== 'undefined'
    ? window.location.pathname.match(/^\/collabora\/([A-Za-z0-9_-]+)\/?$/)
    : null

// Customer review page (Recensioni). Backend builds links as
// `https://<sede-domain>/recensione/<token>`; the bare `/recensione` is a
// small Google invitation for visitors without a personal link. Both are
// client-rendered here so they need no route, OG card or sitemap entry.
const reviewMatch =
  typeof window !== 'undefined'
    ? window.location.pathname.match(/^\/recensione\/([A-Za-z0-9_-]+)\/?$/)
    : null

const reviewInviteMatch =
  typeof window !== 'undefined' ? /^\/recensione\/?$/.test(window.location.pathname) : false

// Client consent to a cross-sede hand-off («Evasione tra sedi»). Backend builds links as
// `https://<sede-domain>/consenso/<token>` (landing.services.passaggi.consenso_url).
const consensoMatch =
  typeof window !== 'undefined' ? window.location.pathname.match(/^\/consenso\/([A-Za-z0-9_-]+)\/?$/) : null

export default function App() {
  const sede = useMemo(() => {
    // Prod / explicit dev: VITE_SITE_SLUG was baked into the bundle.
    if (BUILD_SLUG) {
      return ALL_SEDI.find((s) => s.slug === BUILD_SLUG) ?? DEFAULT_SEDE
    }
    // Ad-hoc dev (plain `npm run dev`): fall back to hostname/port.
    return getCurrentSede()
  }, [])

  useEffect(() => {
    applySedeTheme(sede)
  }, [sede])

  // Cookie-less analytics — only on the marketing site, never on the
  // customer token pages (share/preventivo, conferma, collabora, recensione, consenso).
  useEffect(() => {
    if (sharePreventivoMatch || confirmSlotMatch || collaboraMatch || reviewMatch || reviewInviteMatch || consensoMatch) return
    return initCollector(sede)
  }, [sede])

  // Mobile keyboard fix: use visualViewport API to detect keyboard
  // and prevent the browser from doing insane scroll jumps.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return

    const onFocusIn = (e: Event) => {
      const el = e.target
      if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) return

      const checkAndScroll = () => {
        const vv = window.visualViewport
        if (!vv) return
        const rect = el.getBoundingClientRect()
        const visibleBottom = vv.height + vv.offsetTop
        if (rect.bottom > visibleBottom - 20 || rect.top < vv.offsetTop) {
          const targetY = window.scrollY + rect.top - (vv.height * 0.3)
          window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' })
        }
      }

      setTimeout(checkAndScroll, 300)
      setTimeout(checkAndScroll, 600)
    }

    document.addEventListener('focusin', onFocusIn, { capture: true })
    return () => document.removeEventListener('focusin', onFocusIn, { capture: true })
  }, [])

  // Preventivo share page wins over the sede landing. The backend builds
  // links as `https://<sede-domain>/share/preventivo/<token>` — this is
  // the per-sede side of that contract.
  if (sharePreventivoMatch) {
    return <SharePreventivoPage token={sharePreventivoMatch[1]} />
  }

  // Sopralluogo slot confirmation page. Backend builds links as
  // `https://<sede-domain>/conferma/<token>` (see _send_proposal_email).
  if (confirmSlotMatch) {
    return <ConfirmSlotPage token={confirmSlotMatch[1]} sede={sede} />
  }

  // Contractor "Collabora" page. Backend builds links as
  // `https://<sede-domain>/collabora/<uuid>`. Same same-origin API-proxy
  // contract as the preventivo/confirm pages (see nginx.conf.example).
  if (collaboraMatch) {
    return <CollaboraPage token={collaboraMatch[1]} />
  }

  // Review pages (Recensioni). API calls go cross-origin to backendBaseUrl,
  // like the contact form, so no nginx proxy block is involved.
  if (reviewMatch) {
    return <ReviewPage token={reviewMatch[1]} sede={sede} />
  }
  if (reviewInviteMatch) {
    return <ReviewInvitePage sede={sede} />
  }

  // Consent page («Evasione tra sedi»). Same same-origin API-proxy contract as the
  // confirm page: `/api/landing/passaggi/consenso/` is forwarded to avmg.it (nginx.conf.example).
  if (consensoMatch) {
    return <ConsensoPage token={consensoMatch[1]} sede={sede} />
  }

  // Build-time short-circuit: when VITE_SITE_SLUG is set, the other
  // branch is a constant-false comparison that Rollup/Terser can fold
  // away — the unused VerdeXApp import becomes unreferenced and gets
  // tree-shaken out of the per-site bundle.
  if (BUILD_SLUG === 'verde-vercelli-2') return <VerdeVercelli2App sede={sede} />
  if (BUILD_SLUG === 'verde-valsesia-2') return <VerdeValsesia2App sede={sede} />
  if (BUILD_SLUG === 'verde-vercelli') return <VerdeVercelliApp sede={sede} />

  // Dev fallback (BUILD_SLUG === ''): runtime dispatch by detected sede.
  switch (sede.slug) {
    case 'verde-vercelli-2':
      return <VerdeVercelli2App sede={sede} />
    case 'verde-valsesia-2':
      return <VerdeValsesia2App sede={sede} />
    default:
      return <VerdeVercelliApp sede={sede} />
  }
}
